import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "../supabase";
import {
  cacheArrangement,
  cacheGuideTimeline,
  cacheSetlist,
  cacheSongSections,
  getCachedArrangement,
  getCachedArrangementSections,
  getCachedGuideTimeline,
  getCachedSetlist,
  getCachedSongSections,
} from "../dbSafe";
import LyricsDisplay from "./LyricsDisplay";
import DisplayControls from "./DisplayControls";
import useDisplaySettings from "./useDisplaySettings";
import "./display.css";
import { normalizeLine } from "./LyricsDisplay";
import useProjectorTimelineSync from "./hooks/useProjectorTimelineSync";
import useLeaderPresence from "../hooks/useLeaderPresence";
import useFollowerTransportSync from "../hooks/useFollowerTransportSync";
import { buildMeasureBoundaryBeats } from "../utils/timing";
import { getCapabilities } from "../capabilities";

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const TRANSPORT_STATE_KEY = "ct-transport-state";

const normalizeSetlist = (raw = []) =>
  raw
    .map((item) => ({
      id: String(item.songid || item.songs?.id || item.id),
      title: item.songs?.title || item.title || "Untitled",
      artist: item.songs?.artist || item.artist || "",
      band_id: item.songs?.band_id || item.band_id || null,
      chordpro: item.songs?.chordpro || item.chordpro || "",
      skey: item.custom_key || item.songs?.skey || item.skey || "C",
      order: item.order ?? 0,
    }))
    .sort((a, b) => (a.order || 0) - (b.order || 0));

async function fetchSetlistOnline(serviceId) {
  const { data, error } = await supabase
    .from("servicessongs")
    .select(
      `
        id,
        songid,
        order,
        custom_key,
        songs (
          id,
          title,
          artist,
          skey,
          chordpro,
          band_id
        )
      `,
    )
    .eq("serviceid", serviceId)
    .order("order", { ascending: true });

  if (error) throw error;
  await cacheSetlist(serviceId, data || []);
  return normalizeSetlist(data || []);
}

async function fetchSectionsOnline(songId, bandId, serviceId) {
  const normalizedSongId = Number.isNaN(Number(songId)) ? songId : Number(songId);
  const sectionPromise = supabase.from("song_sections").select("*").eq("song_id", normalizedSongId).order("section_order", { ascending: true });
  let sectionRes = { data: [], error: null };
  let serviceArrRes = { data: null };
  let defaultArrRes = { data: null };

  [sectionRes] = await Promise.all([sectionPromise]);

  // Only attempt arrangement lookups when bandId is present (avoids null-key errors)
  if (bandId) {
    const serviceQuery = supabase
      .from("song_arrangements")
      .select("*")
      .eq("song_id", normalizedSongId)
      .eq("band_id", bandId)
      .eq("service_id", serviceId)
      .maybeSingle();
    const defaultQuery = supabase
      .from("song_arrangements")
      .select("*")
      .eq("song_id", normalizedSongId)
      .eq("band_id", bandId)
      .is("service_id", null)
      .maybeSingle();
    [serviceArrRes, defaultArrRes] = await Promise.all([serviceQuery, defaultQuery]);
  }

  const sectionError = sectionRes.error;
  if (!sectionError && sectionRes.data?.length) {
    await cacheSongSections(songId, sectionRes.data);
  }

  const arrangement = serviceArrRes.data || defaultArrRes.data || null;
  let arrSections = null;
  if (arrangement) {
    const { data: arrData, error: arrErr } = await supabase
      .from("song_arrangements_sections")
      .select(
        `
        id,
        arrangement_id,
        section_id,
        section_order,
        song_sections!inner (
          id,
          section_label,
          content,
          section_order
        )
      `,
      )
      .eq("arrangement_id", arrangement.id)
      .order("section_order", { ascending: true });

    if (!arrErr && arrData) {
      arrSections = arrData;
      await cacheArrangement(arrangement, arrData);
    }
  }

  const baseSections = sectionRes.data || [];
  if (arrangement && arrSections) {
    const normalized = arrSections.map((as) => ({
      ...as.song_sections,
      arrangement_order: as.section_order,
      arrangement_section_id: as.id,
    }));
    return { sections: normalized, arrangementId: arrangement.id };
  }

  return { sections: baseSections, arrangementId: arrangement?.id || null };
}

async function fetchBandIdOnline(songId) {
  if (!songId) return null;
  const normalizedSongId = Number.isNaN(Number(songId)) ? songId : Number(songId);
  try {
    const { data, error } = await supabase
      .from("songs")
      .select("band_id")
      .eq("id", normalizedSongId)
      .single();
    if (error) throw error;
    return data?.band_id || null;
  } catch (err) {
    console.warn("Band lookup failed:", err?.message || err);
    return null;
  }
}

async function fetchArrangementIdOnline(songId, bandId, serviceId) {
  if (!bandId || !songId) return null;
  const normalizedSongId = Number.isNaN(Number(songId)) ? songId : Number(songId);
  try {
    const serviceQuery = supabase
      .from("song_arrangements")
      .select("id")
      .eq("song_id", normalizedSongId)
      .eq("band_id", bandId)
      .eq("service_id", serviceId)
      .maybeSingle();
    const defaultQuery = supabase
      .from("song_arrangements")
      .select("id")
      .eq("song_id", normalizedSongId)
      .eq("band_id", bandId)
      .is("service_id", null)
      .maybeSingle();
    const [serviceArrRes, defaultArrRes] = await Promise.all([serviceQuery, defaultQuery]);
    return serviceArrRes.data?.id || defaultArrRes.data?.id || null;
  } catch (err) {
    console.warn("Arrangement lookup failed:", err?.message || err);
    return null;
  }
}

async function loadSectionsCached(songId, bandId, serviceId) {
  if (!songId) return { sections: [], arrangementId: null };
  const normalizedSongId = Number.isNaN(Number(songId)) ? songId : Number(songId);
  try {
    if (bandId) {
      const arrangement = await getCachedArrangement(normalizedSongId, bandId, serviceId);
      if (arrangement) {
        const arrSections = await getCachedArrangementSections(arrangement.id);
        if (arrSections?.length) {
          return { sections: arrSections, arrangementId: arrangement.id };
        }
        return { sections: [], arrangementId: arrangement.id };
      }
    }
  } catch (err) {
    console.warn("Cached arrangement lookup failed:", err?.message || err);
  }

  try {
    const cachedSections = await getCachedSongSections(normalizedSongId);
    if (cachedSections?.length) return { sections: cachedSections, arrangementId: null };
  } catch (err) {
    console.warn("Cached sections lookup failed:", err?.message || err);
  }

  return { sections: [], arrangementId: null };
}

const navLabelRegex =
  /^(verse|v|chorus|c|intro|interlude|instrumental|inst|bridge|tag|outro|ending|refrain|pre[- ]?chorus|prechorus|vamp|solo)(\s+\d+)?[:.]?$/i;

const hasRenderableLyrics = (content = "") => {
  if (!content.trim()) return false;
  const withoutChords = content
    .replace(/\[[^\]]+\]/g, "")
    .replace(/\{[^}]+\}/g, "")
    .replace(/\r\n/g, "\n");
  const lines = withoutChords.split("\n").map((l) => l.trim());
  return lines.some((line) => line && !navLabelRegex.test(line));
};

const buildVisibleSections = (song) => {
  if (!song) return [];
  const base = song.sections || [];
  const nonEmpty = base.filter((section) => {
    if (!section) return false;
    const label = section.section_label || "";
    const isIntro = /^intro(\s|$)/i.test(label);
    if (isIntro) return true;
    return hasRenderableLyrics(section?.content || "");
  });
  if (nonEmpty.length) {
    return nonEmpty.map((section) => {
      if (section.content && hasRenderableLyrics(section.content)) return section;
      return {
        ...section,
        content: song.title || "Song",
      };
    });
  }
  if (song?.chordpro?.trim() && hasRenderableLyrics(song.chordpro)) {
    return [
      {
        id: `mono-${song.id}`,
        section_label: song.title || "Song",
        content: song.chordpro,
      },
    ];
  }
  return [];
};

const extractTempoFromChordPro = (chordPro = "") => {
  const match = chordPro.match(/\{tempo:\s*([0-9]+)\s*\}/i);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const DisplayApp = () => {
  const capabilities = React.useMemo(() => getCapabilities(), []);
  const limitedMode = capabilities.forceLite;
  const limitedReason = capabilities.limitedReason;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const rawServiceId = searchParams.get("setlist");
  const serviceId = React.useMemo(() => {
    if (!rawServiceId) return null;
    const asNumber = Number(rawServiceId);
    return Number.isNaN(asNumber) ? rawServiceId : asNumber;
  }, [rawServiceId]);
  const [songs, setSongs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [currentSongIndex, setCurrentSongIndex] = React.useState(0);
  const [currentSectionIndex, setCurrentSectionIndex] = React.useState(0);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [isBlackout, setIsBlackout] = React.useState(false);
  const [guideTimeline, setGuideTimeline] = React.useState(null);
  const [followArrangementId, setFollowArrangementId] = React.useState(null);
  const [transportState, setTransportState] = React.useState(null);
  const [projectorClockActive, setProjectorClockActive] = React.useState(false);
  const [presenceBandId, setPresenceBandId] = React.useState(null);
  const [hiddenLinesMap, setHiddenLinesMap] = React.useState(() => {
    try {
      const raw = localStorage.getItem("display-hidden-lines");
      if (raw) return JSON.parse(raw);
    } catch (_) {
      /* ignore */
    }
    return {};
  });
  const lyricsRef = React.useRef(null);
  const scrollRaf = React.useRef(null);
  const transportRawRef = React.useRef("");
  const projectorClockTimerRef = React.useRef(null);
  const projectorClockStartMsRef = React.useRef(0);
  const projectorClockStartBeatRef = React.useRef(0);
  const lastSongIndexRef = React.useRef(0);
  const lastProjectorIndexRef = React.useRef(null);
  const lastLeaderSongIdRef = React.useRef(null);
  const manualOverrideRef = React.useRef({
    active: false,
    lockedAutoSectionId: null,
    lastAutoSectionId: null,
  });

  const { settings, updateSetting, resetSettings, recentBackgroundImages, addRecentBackgroundImage } = useDisplaySettings();

  const currentSong = songs[currentSongIndex];
  const effectiveArrangementId = currentSong?.arrangementId || followArrangementId || null;
  const visibleSections = React.useMemo(() => buildVisibleSections(currentSong), [currentSong]);

  const currentSection = visibleSections[currentSectionIndex] || null;
  const sectionIndexById = React.useMemo(() => {
    const map = new Map();
    visibleSections.forEach((section, idx) => {
      if (!section) return;
      const id = String(section.id ?? section.section_id ?? section.arrangement_section_id ?? "");
      if (id) map.set(id, idx);
    });
    return map;
  }, [visibleSections]);
  const currentSongHidden = React.useMemo(() => {
    const id = currentSong?.id;
    const list = (id && hiddenLinesMap[id]) || [];
    return new Set(list);
  }, [currentSong?.id, hiddenLinesMap]);
  const isMonolithicFallback = React.useMemo(() => {
    if (visibleSections.length !== 1) return false;
    const only = visibleSections[0];
    const id = String(only?.id ?? "");
    return id.startsWith("mono-") || id.startsWith("fallback-");
  }, [visibleSections]);

  const {
    activeLeaders,
    selectedLeaderId,
    setSelectedLeaderId,
    isOnline: isPresenceOnline,
    requestLeaderPresence,
  } = useLeaderPresence({
    bandId: presenceBandId,
    isLeader: false,
    leaderUserId: null,
    leaderName: null,
  });

  const isLitePlaybackActive = Boolean(transportState?.isLitePlaybackActive);
  const transportStartAt = Number.isFinite(transportState?.sectionStartAt)
    ? transportState.sectionStartAt
    : transportState?.transportStartAt;
  const transportStartBeat = Number.isFinite(Number(transportState?.sectionStartBeat))
    ? Number(transportState?.sectionStartBeat)
    : 0;
  const transportClock = React.useMemo(
    () =>
      transportState
        ? {
            audioTimeSec: transportState.audioTimeSec,
            updatedAtMs: transportState.updatedAtMs,
          }
        : null,
    [transportState?.audioTimeSec, transportState?.updatedAtMs],
  );

  const stopFollowerDisplayPlayback = React.useCallback(() => {
    if (projectorClockActive) {
      setProjectorClockActive(false);
    }
    setTransportState({ isLitePlaybackActive: false, updatedAtMs: Date.now() });
  }, [projectorClockActive]);

  React.useEffect(() => {
    lastLeaderSongIdRef.current = null;
    setFollowArrangementId(null);
  }, [selectedLeaderId]);

  React.useEffect(() => {
    setSelectedLeaderId(null);
  }, [setSelectedLeaderId]);

  React.useEffect(() => {
    if (selectedLeaderId) {
      stopFollowerDisplayPlayback();
    }
  }, [selectedLeaderId, stopFollowerDisplayPlayback]);

  React.useEffect(() => {
    if (!isPresenceOnline && selectedLeaderId) {
      setSelectedLeaderId(null);
    }
  }, [isPresenceOnline, selectedLeaderId, setSelectedLeaderId]);

  React.useEffect(() => () => {
    setSelectedLeaderId(null);
  }, [setSelectedLeaderId]);
  const timelineSections = React.useMemo(() => {
    if (!guideTimeline || guideTimeline.ok === false) return [];
    return guideTimeline.sections || [];
  }, [guideTimeline]);
  const timelineBpm = React.useMemo(() => {
    const value = Number(guideTimeline?.bpm || guideTimeline?.tempo);
    if (Number.isFinite(value) && value > 0) return value;
    return extractTempoFromChordPro(currentSong?.chordpro || "");
  }, [currentSong?.chordpro, guideTimeline?.bpm, guideTimeline?.tempo]);
  const timelineBeatsPerMeasure = React.useMemo(() => {
    const value = Number(guideTimeline?.beats_per_measure || guideTimeline?.beatsPerMeasure);
    return Number.isFinite(value) && value > 0 ? value : 4;
  }, [guideTimeline?.beats_per_measure, guideTimeline?.beatsPerMeasure]);
  const measureBoundaries = React.useMemo(
    () => buildMeasureBoundaryBeats(timelineSections, timelineBeatsPerMeasure),
    [timelineSections, timelineBeatsPerMeasure],
  );
  const timelineSectionsById = React.useMemo(() => {
    const map = new Map();
    timelineSections.forEach((section) => {
      const id = String(section?.section_id ?? section?.id ?? "");
      if (id) map.set(id, section);
    });
    return map;
  }, [timelineSections]);

  const getSectionIndexById = React.useCallback(
    (sectionId) => {
      const id = String(sectionId ?? "");
      const index = visibleSections.findIndex((section) => String(section?.id ?? "") === id);
      return index >= 0 ? index : null;
    },
    [visibleSections],
  );

  const setCurrentSongSafe = React.useCallback(
    (nextIndex) => {
      if (!songs.length) return;
      const wrapped = ((nextIndex % songs.length) + songs.length) % songs.length;
      setCurrentSongIndex(wrapped);
      setCurrentSectionIndex(0);
    },
    [songs.length],
  );

  const markManualOverride = React.useCallback(() => {
    const override = manualOverrideRef.current;
    override.active = true;
    override.lockedAutoSectionId =
      override.lastAutoSectionId || String(currentSection?.id ?? "");
  }, [currentSection?.id]);

  React.useEffect(() => {
    const count = visibleSections.length;
    if (!count) {
      setCurrentSectionIndex(0);
      return;
    }
    setCurrentSectionIndex((idx) => Math.min(Math.max(idx, 0), count - 1));
  }, [visibleSections.length]);

  const scrollToTop = React.useCallback(() => {
    if (lyricsRef.current) {
      lyricsRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, []);

  const loadData = React.useCallback(async () => {
    if (!serviceId) {
      setError("Missing setlist id (?setlist=123)");
      setSongs([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      let baseSongs = await loadSetlistFromCache(serviceId);
      if (!baseSongs.length) {
        baseSongs = await fetchSetlistOnline(serviceId);
      }

      const songsWithSections = await Promise.all(
        baseSongs.map(async (song) => {
          const { sections, arrangementId } = await loadSongSections(song, serviceId);
          return { ...song, sections, arrangementId };
        }),
      );

      setSongs(songsWithSections);
      setCurrentSongIndex(0);
      setCurrentSectionIndex(0);
    } catch (err) {
      console.error("Display load error:", err);
      setError(err.message || "Failed to load setlist");
    } finally {
      setLoading(false);
    }
  }, [serviceId]);

  const readTransportState = React.useCallback(() => {
    try {
      const raw = localStorage.getItem(TRANSPORT_STATE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }, []);

  const persistHidden = React.useCallback((next) => {
    setHiddenLinesMap(next);
    try {
      localStorage.setItem("display-hidden-lines", JSON.stringify(next));
    } catch (_) {
      /* ignore */
    }
  }, []);

  const hideLineForSong = React.useCallback(
    (line) => {
      const songId = currentSong?.id;
      if (!songId || !line) return;
      const norm = normalizeLine(line);
      if (!norm) return;
      setHiddenLinesMap((prev) => {
        const existing = prev[songId] || [];
        if (existing.includes(norm)) return prev;
        const next = { ...prev, [songId]: [...existing, norm] };
        persistHidden(next);
        return next;
      });
    },
    [currentSong?.id, persistHidden],
  );

  const unhideLineForSong = React.useCallback(
    (line) => {
      const songId = currentSong?.id;
      if (!songId) return;
      const norm = normalizeLine(line);
      setHiddenLinesMap((prev) => {
        const existing = prev[songId] || [];
        const filtered = existing.filter((l) => l !== norm);
        const next = { ...prev, [songId]: filtered };
        persistHidden(next);
        return next;
      });
    },
    [currentSong?.id, persistHidden],
  );

  const clearHiddenForSong = React.useCallback(() => {
    const songId = currentSong?.id;
    if (!songId) return;
    setHiddenLinesMap((prev) => {
      if (!prev[songId]?.length) return prev;
      const next = { ...prev };
      delete next[songId];
      persistHidden(next);
      return next;
    });
  }, [currentSong?.id, persistHidden]);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    if (presenceBandId || !songs.length) return;
    const firstBand = songs.find((song) => song?.band_id)?.band_id;
    if (firstBand) {
      setPresenceBandId(firstBand);
    }
  }, [presenceBandId, songs]);

  React.useEffect(() => {
    let cancelled = false;
    const bandId = currentSong?.band_id;
    if (bandId) {
      setPresenceBandId(bandId);
      return () => {
        cancelled = true;
      };
    }
    if (!currentSong?.id) {
      setPresenceBandId(null);
      return () => {
        cancelled = true;
      };
    }
    const loadBandId = async () => {
      const fetched = await fetchBandIdOnline(currentSong.id);
      if (cancelled) return;
      setPresenceBandId(fetched || null);
    };
    loadBandId();
    return () => {
      cancelled = true;
    };
  }, [currentSong?.band_id, currentSong?.id]);

  React.useEffect(() => {
    if (projectorClockActive) return undefined;
    const update = () => {
      const raw = localStorage.getItem(TRANSPORT_STATE_KEY) || "";
      if (raw === transportRawRef.current) return;
      transportRawRef.current = raw;
      const parsed = readTransportState();
      setTransportState(parsed);
    };
    update();
    const intervalId = setInterval(update, 500);
    const onStorage = (event) => {
      if (event.key !== TRANSPORT_STATE_KEY) return;
      update();
    };
    window.addEventListener("storage", onStorage);
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("storage", onStorage);
    };
  }, [projectorClockActive, readTransportState]);

  React.useEffect(() => {
    let cancelled = false;
    const arrangementId = effectiveArrangementId;
    if (!arrangementId) {
      if (currentSong?.id) {
        console.log("[projector-sync] missing arrangement", {
          songId: currentSong.id,
          bandId: currentSong.band_id || null,
          serviceId,
        });
      }
      setGuideTimeline(null);
      return () => {
        cancelled = true;
      };
    }
    const loadTimeline = async () => {
      const cached = await getCachedGuideTimeline(arrangementId);
      if (cancelled) return;
      if (cached) {
        setGuideTimeline(cached);
        return;
      }
      try {
        const { data, error: rpcError } = await supabase.rpc("build_guide_timeline", {
          p_arrangement_id: arrangementId,
        });
        if (cancelled) return;
        if (rpcError) throw rpcError;
        if (data) {
          console.log("[projector-sync] timeline loaded", {
            arrangementId,
            bpm: data?.bpm || data?.tempo || null,
            sectionCount: data?.sections?.length || 0,
          });
          setGuideTimeline(data);
          await cacheGuideTimeline(arrangementId, data);
        }
      } catch (err) {
        if (cancelled) return;
        setGuideTimeline(null);
        console.warn("Guide timeline fetch failed:", err?.message || err);
      }
    };
    loadTimeline();
    return () => {
      cancelled = true;
    };
  }, [currentSong?.band_id, currentSong?.id, effectiveArrangementId, serviceId]);

  React.useEffect(() => {
    if (!projectorClockActive) {
      if (projectorClockTimerRef.current) {
        clearInterval(projectorClockTimerRef.current);
        projectorClockTimerRef.current = null;
      }
      return;
    }

    const tick = () => {
      const now = Date.now();
      const elapsedSec = Math.max(0, (now - projectorClockStartMsRef.current) / 1000);
      setTransportState({
        isLitePlaybackActive: true,
        transportStartAt: 0,
        sectionStartAt: 0,
        sectionStartBeat: projectorClockStartBeatRef.current || 0,
        audioTimeSec: elapsedSec,
        updatedAtMs: now,
      });
    };

    tick();
    projectorClockTimerRef.current = setInterval(tick, 250);
    return () => {
      if (projectorClockTimerRef.current) {
        clearInterval(projectorClockTimerRef.current);
        projectorClockTimerRef.current = null;
      }
    };
  }, [projectorClockActive]);

  React.useEffect(() => {
    scrollToTop();
  }, [currentSongIndex, currentSectionIndex, scrollToTop]);

  React.useEffect(() => {
    manualOverrideRef.current.active = false;
    manualOverrideRef.current.lockedAutoSectionId = null;
  }, [currentSong?.id]);

  React.useEffect(() => {
    const prevIndex = lastSongIndexRef.current;
    if (prevIndex !== currentSongIndex) {
      lastSongIndexRef.current = currentSongIndex;
      if (projectorClockActive) {
        setProjectorClockActive(false);
        setTransportState({ isLitePlaybackActive: false, updatedAtMs: Date.now() });
        console.log("[projector-clock] stopped (song change)");
      }
      return;
    }
    lastSongIndexRef.current = currentSongIndex;
  }, [currentSongIndex, projectorClockActive]);

  React.useEffect(() => {
    if (!settings.autoScroll || !isMonolithicFallback) {
      if (scrollRaf.current) cancelAnimationFrame(scrollRaf.current);
      return;
    }

    const step = () => {
      if (!lyricsRef.current) return;
      const container = lyricsRef.current;
      const maxScroll = container.scrollHeight - container.clientHeight;
      const next = Math.min(
        container.scrollTop + settings.scrollSpeed,
        maxScroll,
      );
      container.scrollTop = next;
      if (next < maxScroll) {
        scrollRaf.current = requestAnimationFrame(step);
      }
    };

    scrollRaf.current = requestAnimationFrame(step);
    return () => {
      if (scrollRaf.current) cancelAnimationFrame(scrollRaf.current);
    };
  }, [settings.autoScroll, settings.scrollSpeed, currentSectionIndex, currentSongIndex, isMonolithicFallback]);

  const nextSection = React.useCallback(() => {
    const count = visibleSections.length;
    if (!count) return;
    markManualOverride();
    setCurrentSectionIndex((i) => {
      if (i < count - 1) return i + 1;
      // auto-advance to next song when past last section
      setCurrentSongSafe(currentSongIndex + 1);
      return 0;
    });
  }, [visibleSections.length, currentSongIndex, markManualOverride, setCurrentSongSafe]);

  const prevSection = React.useCallback(() => {
    const count = visibleSections.length;
    if (!count) return;
    markManualOverride();
    setCurrentSectionIndex((i) => {
      if (i > 0) return i - 1;
      if (!songs.length) return 0;
      const prevIndex = ((currentSongIndex - 1) % songs.length + songs.length) % songs.length;
      const prevSong = songs[prevIndex];
      const prevVisible = buildVisibleSections(prevSong);
      setCurrentSongIndex(prevIndex);
      return Math.max(0, prevVisible.length - 1);
    });
  }, [visibleSections.length, currentSongIndex, markManualOverride, songs]);

  const nextSong = React.useCallback(() => {
    markManualOverride();
    setCurrentSongSafe(currentSongIndex + 1);
  }, [currentSongIndex, markManualOverride, setCurrentSongSafe]);

  const prevSong = React.useCallback(() => {
    markManualOverride();
    setCurrentSongSafe(currentSongIndex - 1);
  }, [currentSongIndex, markManualOverride, setCurrentSongSafe]);

  const adjustSpeed = React.useCallback(
    (delta) => {
      const nextSpeed = clamp(settings.scrollSpeed + delta, 0, 5);
      updateSetting("scrollSpeed", Number(nextSpeed.toFixed(2)));
    },
    [settings.scrollSpeed, updateSetting],
  );

  const handleAutoSectionIndex = React.useCallback((nextIndex, sectionId) => {
    if (!Number.isFinite(nextIndex)) return;
    if (lastProjectorIndexRef.current === nextIndex) return;
    const override = manualOverrideRef.current;
    if (override?.active) {
      if (override.lockedAutoSectionId === sectionId) {
        return;
      }
      override.active = false;
      override.lockedAutoSectionId = null;
    }
    lastProjectorIndexRef.current = nextIndex;
    setCurrentSectionIndex((prev) => {
      if (prev === nextIndex) return prev;
      console.log("[projector-sync] sectionIndex", nextIndex);
      return nextIndex;
    });
  }, []);

  const toggleProjectorClock = React.useCallback(() => {
    if (projectorClockActive) {
      setProjectorClockActive(false);
      setTransportState({ isLitePlaybackActive: false, updatedAtMs: Date.now() });
      console.log("[projector-clock] stopped");
      return;
    }
    const currentId = String(currentSection?.id ?? "");
    const sectionStartBeat = Number(timelineSectionsById.get(currentId)?.start_beat || 0);
    projectorClockStartBeatRef.current = Number.isFinite(sectionStartBeat) ? sectionStartBeat : 0;
    projectorClockStartMsRef.current = Date.now();
    manualOverrideRef.current.active = false;
    manualOverrideRef.current.lockedAutoSectionId = null;
    setProjectorClockActive(true);
    setDrawerOpen(false);
    console.log("[projector-clock] started", {
      bpm: timelineBpm,
      sectionId: currentId || null,
      sectionStartBeat: projectorClockStartBeatRef.current,
      sectionCount: timelineSections.length,
      arrangementId: currentSong?.arrangementId || null,
      hasTimeline: Boolean(guideTimeline),
    });
  }, [currentSection?.id, currentSong?.arrangementId, guideTimeline, projectorClockActive, timelineBpm, timelineSections.length, timelineSectionsById]);

  const toggleFullScreen = React.useCallback(() => {
    const el = document.documentElement;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  React.useEffect(() => {
    const onKeyDown = (e) => {
      switch (e.key) {
        case " ":
          e.preventDefault();
          nextSection();
          break;
        case "ArrowRight":
          e.preventDefault();
          nextSection();
          break;
        case "ArrowLeft":
          e.preventDefault();
          prevSection();
          break;
        case "ArrowUp":
          if (e.shiftKey) {
            handleManualScroll(-1);
          } else {
            adjustSpeed(0.1);
          }
          break;
        case "ArrowDown":
          if (e.shiftKey) {
            handleManualScroll(1);
          } else {
            adjustSpeed(-0.1);
          }
          break;
        case "b":
        case "B":
          setIsBlackout((prev) => !prev);
          break;
        case "p":
        case "P":
          toggleProjectorClock();
          break;
        case "f":
        case "F":
          toggleFullScreen();
          break;
        case "Escape":
          setDrawerOpen(false);
          break;
        default:
          break;
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [nextSection, prevSection, adjustSpeed, toggleFullScreen, toggleProjectorClock]);

  const handleManualScroll = React.useCallback((direction = 1) => {
    const el = lyricsRef.current;
    if (!el) return;
    const step = Math.max(120, el.clientHeight * 0.9);
    el.scrollBy({ top: direction * step, behavior: "smooth" });
  }, []);

  useProjectorTimelineSync({
    timelineSections,
    bpm: timelineBpm,
    isLitePlaybackActive,
    transportStartAt,
    transportStartBeat,
    transportClock,
    sectionIndexById,
    onSectionIndex: handleAutoSectionIndex,
    onEnd: React.useCallback(() => {
      if (projectorClockActive) {
        setProjectorClockActive(false);
        setTransportState({ isLitePlaybackActive: false, updatedAtMs: Date.now() });
        console.log("[projector-clock] stopped (end reached)");
      }
    }, [projectorClockActive]),
    manualOverrideRef,
  });

  useFollowerTransportSync({
    bandId: presenceBandId,
    selectedLeaderId,
    timelineSections,
    measureBoundaryBeats: measureBoundaries?.boundaryArray || [],
    getSectionIndex: getSectionIndexById,
    onSectionIndex: (nextIndex) => {
      setCurrentSectionIndex((prev) => (prev === nextIndex ? prev : nextIndex));
    },
    currentSongId: currentSong?.id,
    currentArrangementId: effectiveArrangementId,
    smoothScroll: false,
    leadInMeasures: -0.5,
    onLeaderSongChange: (payload) => {
      const leaderSongId = payload?.song_id;
      if (!leaderSongId || lastLeaderSongIdRef.current === String(leaderSongId)) return;
      lastLeaderSongIdRef.current = String(leaderSongId);
      if (payload?.arrangement_id) {
        setFollowArrangementId(payload.arrangement_id);
      }
      const nextIndex = songs.findIndex((song) => String(song.id) === String(leaderSongId));
      if (nextIndex >= 0) {
        stopFollowerDisplayPlayback();
        setCurrentSongIndex(nextIndex);
        setCurrentSectionIndex(0);
      }
    },
    onStopFollow: (reason) => {
      if (reason !== "leader-changed") {
        setSelectedLeaderId(null);
      }
    },
  });

  const pageStyle = React.useMemo(
    () => ({
      backgroundColor: settings.backgroundColor,
      color: settings.fontColor,
      backgroundImage: settings.backgroundImage
        ? `url(${settings.backgroundImage})`
        : "none",
    }),
    [settings.backgroundColor, settings.fontColor, settings.backgroundImage],
  );

  return (
    <div className="display-shell" style={pageStyle}>
      <div className="display-header">
        <div className="display-title">
          <span className="display-song">{currentSong?.title || "Display Mode"}</span>
          {currentSection?.section_label && (
            <span className="display-section">
              {currentSection.section_label}
              <button
                type="button"
                className="display-playback"
                onClick={toggleProjectorClock}
                title={projectorClockActive ? "Pause clock" : "Start clock"}
                aria-label={projectorClockActive ? "Pause clock" : "Start clock"}
              >
                {projectorClockActive ? "⏸" : "▶︎"}
              </button>
            </span>
          )}
        </div>
        <div className="display-actions">
          {isPresenceOnline && !selectedLeaderId && activeLeaders.length > 0
            ? activeLeaders.map((leader) => {
                const leaderId = String(leader.leader_user_id);
                return (
                  <button
                    key={leaderId}
                    className="display-btn"
                    onClick={() => setSelectedLeaderId(leaderId)}
                    aria-label={`Follow ${leader.leader_username || "Leader"}`}
                  >
                    Follow {leader.leader_username || "Leader"}
                  </button>
                );
              })
            : null}
          <button
            className="display-btn"
            onClick={() => setDrawerOpen((o) => !o)}
            aria-label="Settings"
          >
            ⚙️
          </button>
          <button
            className="display-btn"
            onClick={() => setIsBlackout((b) => !b)}
            aria-label="Toggle blackout"
          >
            {isBlackout ? "Show" : "Black"}
          </button>
        </div>
      </div>

      <div className="display-body">
        {loading && <div className="display-status">Loading setlist…</div>}
        {error && <div className="display-status error">{error}</div>}
        {!loading && !error && currentSong && currentSection && (
          <div className="display-slide">
            <LyricsDisplay
              key={`${currentSong.id}-${currentSection.id || currentSectionIndex}`}
              section={currentSection}
              settings={settings}
              isBlackout={isBlackout}
              contentRef={lyricsRef}
              hiddenLines={currentSongHidden}
              onHideLine={hideLineForSong}
            />
          </div>
        )}
      </div>

      <DisplayControls
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        settings={settings}
        onSettingChange={updateSetting}
        onReset={resetSettings}
        onPrevSection={prevSection}
        onNextSection={nextSection}
        onPrevSong={prevSong}
        onNextSong={nextSong}
        onToggleProjectorClock={toggleProjectorClock}
        isProjectorClockActive={projectorClockActive}
        isBlackout={isBlackout}
        toggleBlackout={() => setIsBlackout((b) => !b)}
        toggleFullScreen={toggleFullScreen}
        onReturnToApp={() => navigate("/")}
        showAutoScroll={isMonolithicFallback}
        activeLeaders={activeLeaders}
        selectedLeaderId={selectedLeaderId}
        onSelectLeader={setSelectedLeaderId}
        isPresenceOnline={isPresenceOnline}
        onRequestLeaderPresence={requestLeaderPresence}
        recentBackgroundImages={recentBackgroundImages}
        onAddRecentBackgroundImage={addRecentBackgroundImage}
        hiddenLines={Array.from(currentSongHidden)}
        onUnhideLine={unhideLineForSong}
        onClearHidden={clearHiddenForSong}
      />
    </div>
  );
};

async function loadSetlistFromCache(serviceId) {
  if (serviceId == null) return [];

  let cached = await getCachedSetlist(serviceId);

  if ((!cached || !cached.length) && typeof serviceId === "number") {
    cached = await getCachedSetlist(String(serviceId));
  } else if (
    (!cached || !cached.length) &&
    typeof serviceId === "string" &&
    !Number.isNaN(Number(serviceId))
  ) {
    cached = await getCachedSetlist(Number(serviceId));
  }

  if (!cached?.length) return [];
  return normalizeSetlist(cached);
}

async function loadSongSections(song, serviceId) {
  const songId = song.id;
  let bandId = song.band_id;
  if (!bandId) {
    bandId = await fetchBandIdOnline(songId);
  }

  let { sections, arrangementId } = await loadSectionsCached(songId, bandId, serviceId);
  if (sections.length) {
    if (!arrangementId && bandId) {
      arrangementId = await fetchArrangementIdOnline(songId, bandId, serviceId);
    }
    return { sections: normalizeSectionOrder(sections), arrangementId };
  }

  try {
    const fresh = await fetchSectionsOnline(songId, bandId, serviceId);
    if (fresh.sections?.length) {
      sections = fresh.sections;
      arrangementId = fresh.arrangementId;
    }
  } catch (err) {
    console.warn("Failed to fetch sections online:", err?.message || err);
  }

  if (!sections.length && song.chordpro) {
    sections = [
      {
        id: `fallback-${songId}`,
        section_label: "Song",
        content: song.chordpro,
        section_order: 10,
      },
    ];
  }

  if (!sections.length) return { sections: [], arrangementId: arrangementId || null };
  return { sections: normalizeSectionOrder(sections), arrangementId: arrangementId || null };
}

export default DisplayApp;

function normalizeSectionOrder(sections) {
  return [...sections]
    .map((section, idx) => ({
      ...section,
      arrangement_order:
        section.arrangement_order ??
        section.section_order ??
        (idx + 1) * 10,
    }))
    .sort(
      (a, b) =>
        (a.arrangement_order || a.section_order || 0) -
        (b.arrangement_order || b.section_order || 0),
    );
}
